import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorNotification, showWarningNotification } from '@vision/ui/components/VNotification';
import { useSelectedAccountId } from '@vision/ui/hooks';
import { useBenchmarkSetupsQuery } from '@vision/ui/services/benchmark-setups.service';
import { useMatrixCatalogsQuery, useMatrixMeasuresQuery } from './benchmark-comparison.service';
import type { BenchmarkMatrixSelection } from './types';

export function useMatrixSelection(selection: BenchmarkMatrixSelection) {
  const accountId = useSelectedAccountId();
  const setups = useBenchmarkSetupsQuery(accountId, { skip: !accountId, refetchOnMountOrArgChange: true });
  const setup = setups.currentData?.find((item) => item.id === selection.setupId);
  const scope = { accountId, setupId: selection.setupId };
  const measures = useMatrixMeasuresQuery(scope, { skip: !setup, refetchOnMountOrArgChange: true });
  const catalogs = useMatrixCatalogsQuery(
    { ...scope, measureIds: selection.measureIds },
    {
      skip: !setup || !selection.measureIds.length,
      refetchOnMountOrArgChange: true,
    },
  );
  const { t } = useTranslation('page-reports');
  const failed =
    setups.isError || measures.isError || catalogs.isError || catalogs.currentData?.some((row) => row.failed);
  const truncated = catalogs.currentData?.some((row) => row.truncated);
  const notificationId = `benchmark-options-${accountId}-${selection.setupId}`;
  useEffect(() => {
    if (failed) showErrorNotification({ id: notificationId, message: t('benchmarkMatrix.loadError') });
  }, [failed, notificationId, t]);
  useEffect(() => {
    if (truncated)
      showWarningNotification({ id: `${notificationId}-truncated`, message: t('benchmarkMatrix.truncated') });
  }, [truncated, notificationId, t]);

  return { scope, setup, setups, measures, catalogs };
}
